import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { stringify } from 'csv-stringify/browser/esm/sync';
import { CsvError, parse } from 'csv-parse/browser/esm/sync';
import { saveAs } from 'file-saver';
import { CommonService } from '../../../shared/services/common.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  UntypedFormBuilder,
  Validators,
} from '@angular/forms';
import {
  MapService,
  StopLadderStopData,
} from 'src/app/component/shared/services/map.service';
import { MatSelectChange } from '@angular/material/select';
import { environment } from 'src/environments/environment';
import moment, { Moment } from 'moment';
import {
  formRequiredFileType,
  toFormData,
} from 'src/app/component/shared/utils';
import { from, Observable, of, throwError } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { isScheduleValid } from './timetable-validation';
import { HttpErrorResponse } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ProgressTimetableService } from 'src/app/component/shared/services/progress-timetable.service';
import { DriverService } from 'src/app/component/shared/services/openapi/roster';
import {
  RoutePatternDetails,
  RoutePatternLatestValue,
} from '../time-table-types';

const isDuplicate = a => b => Object.keys(a).every(k => a[k] === b[k]);

export interface TimeTableScheduleModalInput {
  routeList: string[];
  driverList: any[];
}

interface RouteDirectionLadder {
  value: string;
  stops: StopLadderStopData[];
}

@Component({
  selector: 'app-timetable-schedule-modal',
  templateUrl: './timetable-schedule-modal.component.html',
  styleUrls: ['./timetable-schedule-modal.component.scss'],
})
export class TimetableScheduleModalComponent implements OnInit {
  constructor(
    private commonService: CommonService,
    public dialogRef: MatDialogRef<TimetableScheduleModalComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: TimeTableScheduleModalInput,
    private formBuilder: UntypedFormBuilder,
    private mapService: MapService,
    private snackbar: MatSnackBar,
    private progressTimetableService: ProgressTimetableService,
    private driverService: DriverService
  ) {
    this.form = this.formBuilder.group({
      routeId: new FormControl('', [Validators.required]),
      tripDirection: new FormControl('', [Validators.required]),
      startTripDate: new FormControl<moment.Moment | null>(null, [
        Validators.required,
      ]),
      endTripDate: new FormControl<moment.Moment | null>(null, [
        Validators.required,
      ]),
      routePattern: new FormControl('', [Validators.required]),
      file: new FormControl(null, [
        Validators.required,
        formRequiredFileType('csv'),
      ]),
    });
  }

  routeList = [];
  routeSuccess = false;
  loading = false;
  // minDate = moment();
  minDate;

  ngOnInit() {
    console.log(this.dialogData);
    const { routeList, driverList } = this.dialogData;
    this.routeList = routeList;
    this.driverList = driverList;
    // this.getDrivers();
  }

  routeDirectionLadder: RouteDirectionLadder[];
  directions = [];
  routePatternDetails: RoutePatternDetails[];
  routeError = 'Failed to load route. Please select the route and try again.';
  async getRouteLadder(value: string) {
    try {
      this.setLoading(true);

      // clear other input
      this.form.get('tripDirection').reset();
      this.form.get('routePattern').reset();

      const routeLadder: any = await this.mapService.getRouteLadder(value);
      if (routeLadder) {
        // get route patterns
        this.getLatestRoutePattern(value).subscribe({
          next: routePatternValue => {
            const { direction } = routeLadder || {};
            const { routeDetails } = routePatternValue;
            this.routePatternDetails = routeDetails;
            this.directions = routeDetails
              .map(detail => detail.tripDirection)
              .sort();
            this.routeDirectionLadder = direction;
            this.routeSuccess = true;
            this.setLoading(false);
            this.directionSuccess = false;
            this.form.get('routePattern').patchValue('');

            // set to first direction immediately
            const currDirection = this.directions[0];
            this.form.get('tripDirection').patchValue(currDirection);
            this.getRoutePattern(currDirection);
          },
          error: err => {
            console.error('Fetch Route Pattern Error: ', err);
            this.routeError = 'Failed to load route.';
            const { error } = err;
            const { message } = error || {};
            if (message) {
              if (message?.includes('No valid route')) {
                this.routeError = 'Failed to load route. Route has no data';
              }
            }

            this.directions = [];
            this.setLoading(false);
            this.routeSuccess = false;

            this.form.get('routeId').updateValueAndValidity();
            this.setError('routeId', { failed: true });
            this.directionSuccess = false;
            console.log(this.form.get('routeId').errors);
          },
        });
      }
    } catch (e) {
      console.error('Fetch Route Ladder Error: ', e);
      this.directions = [];
      this.setLoading(false);
      this.routeError =
        'Failed to load route. Please select the route and try again.';
      this.routeSuccess = false;
      this.form.get('routeId').updateValueAndValidity();
      this.setError('routeId', { failed: true });
      console.log(this.form.get('routeId').errors);
      this.directionSuccess = false;
    }
  }

  getLatestRoutePattern(routeId: string) {
    const agencyId = localStorage.getItem('agencyId');
    const urlAction =
      environment.apiUrl.gtfs +
      `/v1/routePatternValueLatest/${agencyId}/${routeId}`;

    const urlApi$: Observable<RoutePatternLatestValue> =
      this.commonService.commonGetFullUrlAction(urlAction);
    return urlApi$;
  }

  directionError = '';
  directionSuccess = false;
  getRoutePattern(tripDirection: string) {
    // check if trip direction has stops. if not, throw an error
    const currDirection = this.routeDirectionLadder.find(
      dir => dir.value === tripDirection
    );
    if (currDirection) {
      if (currDirection.stops.length < 1) {
        this.directionError = 'Error: Selected Direction has no stops';
        console.error(this.directionError);
        this.setError('tripDirection', { directionError: true });
        this.directionSuccess = false;
        return;
      }
    } else {
      this.directionError = 'Error: Selected Direction has no data';
      console.error(this.directionError);
      this.setError('tripDirection', { directionError: true });
      this.directionSuccess = false;
      return;
    }

    // assign route pattern
    const currentPattern = this.routePatternDetails.find(
      detail => detail.tripDirection === tripDirection
    );

    if (currentPattern) {
      const { routePattern } = currentPattern;
      this.form.get('routePattern').patchValue(routePattern);
      this.directionSuccess = true;
    } else {
      this.directionError = 'Error: Selected Direction has no route data';
      console.error(this.directionError);
      this.setError('tripDirection', { directionError: true });
      this.directionSuccess = false;
      return;
    }
  }

  /**
   *  Need to trigger change detection manually by setting timeout when setting custom error
   *  why does cdr not work for reactive form? need to investigate
   */
  setError(formControlName: string, errorObj: any) {
    setTimeout(() => {
      this.form.get(formControlName).setErrors(errorObj);
    }, 0);

    // this.cdr.detectChanges();
  }

  setLoading(value: boolean) {
    if (value) {
      this.loading = true;
      this.directionError = '';
      this.fileError = '';
      this.form.disable();
    } else {
      this.loading = false;
      this.form.enable();
    }
  }

  generateTemplate() {
    const routeId = this.form.get('routeId').value;
    const tripDirection = this.form.get('tripDirection').value;
    const currDirection = this.routeDirectionLadder.find(
      ladder => ladder.value === tripDirection
    );
    if (currDirection) {
      const mappedSequence = currDirection.stops.map(
        stop => `${stop.stopSequence}:${stop.stopCode}`
      );
      const columns = ['id', ...mappedSequence];
      const dummySequence = mappedSequence.map((stop, index) =>
        index === 0 ? '05:00' : ''
      );
      const sampleContent = ['7891', ...dummySequence];
      const csvOutput = stringify([columns, sampleContent]);
      console.log('Csv Output: ', csvOutput);
      const file = new File(
        [csvOutput],
        `timetable_${routeId}-${tripDirection}_template.csv`
      );
      saveAs.saveAs(file);
    }
  }

  fileError = '';
  fileValidation(): Observable<any> {
    const csvFile = this.form.get('file').value as File;
    const routeId = this.form.get('routeId').value;
    const tripDirection = this.form.get('tripDirection').value;
    const currDirection = this.routeDirectionLadder.find(
      ladder => ladder.value === tripDirection
    );
    const mappedSequence = currDirection.stops.map(
      stop => `${stop.stopSequence}:${stop.stopCode}`
    );
    const columns = ['id', ...mappedSequence];

    // console.log('Validate Start: ', columns, csvFile);
    return from(csvFile.text()).pipe(
      switchMap(output => {
        try {
          const parsedCsv = parse(output, { columns });
          // add file validation here and throw error
          const validationResult = isScheduleValid(
            parsedCsv,
            columns,
            this.driverList
          );
          console.log('valid csv: ', validationResult);

          if (!validationResult.valid) {
            return throwError({
              message: validationResult.message,
              name: 'Error',
            });
          }
          return of({
            routeId,
            filename: csvFile.name,
            direction: tripDirection,
            data: parsedCsv,
            blockIds: validationResult?.blockIds,
          });
        } catch (e) {
          return throwError(e);
        }
      })
    );
  }

  /**
   * form
   */
  form: FormGroup;

  hasError(field: string, error: string) {
    const control = this.form.get(field);
    return control.dirty && control.hasError(error);
  }

  upload() {
    /**
     * Validate file upload first
     */
    this.loading = true;

    const {
      startTripDate,
      endTripDate,
      routeId,
      file,
      tripDirection,
      ...rest
    } = this.form.value;
    const timetableFormData = toFormData(rest);
    const formattedStart = startTripDate.format('YYYY-MM-DD');
    const formattedEnd = endTripDate.format('YYYY-MM-DD');

    const agencyId = localStorage.getItem('agencyId');
    timetableFormData.append('routeId', routeId);
    timetableFormData.append('startTripDate', formattedStart);
    timetableFormData.append('endTripDate', formattedEnd);
    timetableFormData.append('agencyId', agencyId);
    timetableFormData.append('file', file);
    timetableFormData.append('tripDirection', tripDirection);

    const urlAction = environment.apiUrl.gtfs + '/v1/uploadTimetable';
    const upload$ = this.commonService.commonPostFullUrlAction(
      urlAction,
      timetableFormData
    );

    const validateThenSend$ = this.fileValidation().pipe(
      switchMap(validationResult => {
        console.log('Validation output: ', validationResult);
        return upload$.pipe(
          map(uploadResult => [uploadResult, validationResult])
        ); // call upload
        // return of([{ fileId: undefined }, validationResult]); // temp skip upload
      })
    );

    validateThenSend$.subscribe({
      next: ([uploadResult, validationResult]) => {
        const { fileId } = uploadResult;
        const { blockIds } = validationResult;
        /**
         * start websocket to monitor upload
         */
        const filename = file.name;
        const isoStartDay = startTripDate.format('YYYY-MM-DD[T00:00:00Z]');
        const dayDisplay =
          formattedStart === formattedEnd
            ? formattedStart
            : `${formattedStart} to ${formattedEnd}`;
        const isoEndDay = (endTripDate as Moment)
          .clone()
          .add(1, 'day')
          .format('YYYY-MM-DD[T00:00:00Z]');
        console.log(
          'output',
          // { uploadResult, validationResult },
          {
            fileId,
            filename,
            routeId,
            startDay: isoStartDay,
            endDay: isoEndDay,
            blockIds,
          }
        );
        if (fileId) {
          this.progressTimetableService.add({
            fileId,
            filename: `Upload Direction ${tripDirection} (${dayDisplay})`,
            routeId,
            startDay: isoStartDay,
            endDay: isoEndDay,
            blockIds,
          });
        }
        this.loading = false;
        this.dialogRef.close();
      },
      error: (err: CsvError | Error) => {
        if (err instanceof CsvError) {
          const { message, name, code } = err;
          console.error('CSV Parse Error: ', { message, name, code });
          this.fileError = `Parsing Error : ${message}`;
          this.setError('file', { contentError: true });
        } else if (err instanceof HttpErrorResponse) {
          const { error } = err;
          console.error('HTTP Error: ', error, err);
          const { message: errorMessage } = error || {};
          const isRoutePatternError = errorMessage?.includes('RoutePattern');
          if (isRoutePatternError) {
            this.snackbar.open(
              `Timetable already exists for the selected route and date`,
              null,
              {
                duration: 3000,
                panelClass: 'custom-snack-bar-panel-error',
              }
            );
          } else {
            this.snackbar.open(
              `There was a problem uploading the timetable. Please try again.`,
              null,
              {
                duration: 3000,
                panelClass: 'custom-snack-bar-panel-error',
              }
            );
          }
        } else {
          const { message, name } = err;
          console.error('File Validation Error: ', { message, name }, err);
          this.fileError = `Validation Error : ${message}`;
          this.setError('file', { contentError: true });
        }
        this.loading = false;
      },
    });
  }

  driverList = [];
  getDrivers() {
    this.driverService.getdrivers(undefined, 1000, 0).subscribe({
      next: driverList => {
        const mappedList = driverList.map(driver => ({
          userid: driver.userid,
          full_name: driver.full_name,
        }));
        this.driverList = mappedList;
      },
    });
  }
}
